import React from "react";
import { useContent } from "@/context/ContentContext";
import { resolveImg } from "@/lib/api";
import SEO from "@/components/SEO";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import ContactForm from "@/components/site/ContactForm";
import { ArrowRight, EnvelopeSimple, WhatsappLogo, MapPin, InstagramLogo } from "@phosphor-icons/react";

export default function LetsBegin() {
  const content = useContent();
  const c = content?.closing || {};
  const social = content?.social || {};
  const emphasis = c.emphasis || "entirely";
  const title = c.title || "Let's create a home that feels entirely yours.";
  const bg = c.background_image || "/lets-begin-bg.jpg";

  const renderTitle = () => {
    if (emphasis && title.includes(emphasis)) {
      const [before, after] = title.split(emphasis);
      return (
        <>
          {before}
          <em className="italic font-normal text-accent">{emphasis}</em>
          {after}
        </>
      );
    }
    return title;
  };

  return (
    <div className="bg-background min-h-screen">
      <SEO
        title="Let's Begin — Nat Habitat"
        description={c.body || "Start your project with Nat Habitat — a Dubai interior design studio crafting spaces with soul, detail and intention."}
        path="/lets-begin"
        image={content?.seo?.og_image}
      />
      <Nav brand={content?.brand?.name} />

      <section className="relative min-h-[100svh] w-full overflow-hidden flex items-center justify-center" data-testid="lets-begin-hero">
        {bg && (
          <div className="absolute inset-0">
            <img
              src={resolveImg(bg)}
              alt="Warm minimalist interior with botanical shadows"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-background/25" />
          </div>
        )}

        <div className="relative z-10 mx-auto max-w-4xl px-6 md:px-12 py-32 text-center flex flex-col items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-accent">{c.label || "Our Approach"}</p>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-5 flex items-center justify-center gap-4 text-accent/70">
              <span className="h-px w-16 bg-accent/40" />
              <span className="text-sm">✦</span>
              <span className="h-px w-16 bg-accent/40" />
            </div>
          </Reveal>

          <h1 className="mt-10 font-serif font-light text-5xl sm:text-6xl md:text-7xl leading-[1.05] tracking-tight text-foreground">
            {renderTitle()}
          </h1>

          <Reveal delay={0.15}>
            <p className="mt-10 max-w-md text-base md:text-lg leading-relaxed text-foreground/70">{c.body}</p>
          </Reveal>

          <Reveal delay={0.25}>
            <button
              type="button"
              data-testid="lets-begin-cta"
              onClick={() => document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-14 inline-flex items-center gap-6 rounded-full border border-accent/40 px-12 py-5 text-sm uppercase tracking-[0.2em] text-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors duration-500"
            >
              {c.cta || "Let's begin"}
              <ArrowRight size={18} />
            </button>
          </Reveal>
        </div>
      </section>

      <section id="enquiry" data-testid="enquiry-section" className="bg-background py-24 md:py-36">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 md:gap-16">
            <div className="md:col-span-4">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.3em] text-accent">Start a Project</p>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                  Tell us about your space.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-foreground/60 leading-relaxed">
                  Share a few details and we'll be in touch to begin the conversation.
                </p>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-10 space-y-5" data-testid="contact-details">
                  {social.email && (
                    <a href={`mailto:${social.email}`} className="flex items-center gap-4 group" data-testid="details-email">
                      <span className="h-11 w-11 rounded-full border border-accent/40 grid place-items-center text-accent shrink-0"><EnvelopeSimple size={18} /></span>
                      <span className="text-foreground/80 group-hover:text-foreground transition-colors">{social.email}</span>
                    </a>
                  )}
                  {social.whatsapp && (
                    <a href={social.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="details-whatsapp">
                      <span className="h-11 w-11 rounded-full border border-accent/40 grid place-items-center text-accent shrink-0"><WhatsappLogo size={18} /></span>
                      <span className="text-foreground/80 group-hover:text-foreground transition-colors">{social.phone || "WhatsApp"}</span>
                    </a>
                  )}
                  {social.instagram && (
                    <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="details-instagram">
                      <span className="h-11 w-11 rounded-full border border-accent/40 grid place-items-center text-accent shrink-0"><InstagramLogo size={18} /></span>
                      <span className="text-foreground/80 group-hover:text-foreground transition-colors">Instagram</span>
                    </a>
                  )}
                  {(content?.footer?.location) && (
                    <div className="flex items-center gap-4" data-testid="details-location">
                      <span className="h-11 w-11 rounded-full border border-accent/40 grid place-items-center text-accent shrink-0"><MapPin size={18} /></span>
                      <span className="text-foreground/80">{content.footer.location}</span>
                    </div>
                  )}
                </div>
              </Reveal>
            </div>

            <div className="md:col-span-7 md:col-start-6">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <Footer content={content} />
    </div>
  );
}
